import { useId } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import type { FiltrosAuditoria, ResumenAuditoria } from "@/lib/auditoria";
import type { Obra } from "@/lib/obras";
import type { Usuario } from "@/lib/usuarios";

type Props = {
  valor: FiltrosAuditoria;
  onChange: (filtros: FiltrosAuditoria) => void;
  usuarios: Usuario[];
  obras: Obra[];
  resumen?: ResumenAuditoria;
};

/**
 * AuditoriaFiltros son los filtros del visor (#254, D6): actor, acción, obra, tabla, id de
 * registro, rango de fechas e IP. Se combinan con AND en el servidor. El catálogo de
 * acciones y tablas viene del resumen —constantes del código MÁS los valores realmente
 * presentes—, de modo que las acciones históricas ya retiradas (LOTO_EJECUTAR…) siguen
 * siendo filtrables.
 */
export function AuditoriaFiltros({ valor, onChange, usuarios, obras, resumen }: Props) {
  const id = useId();
  const set = (campo: keyof FiltrosAuditoria) => (v: string) => onChange({ ...valor, [campo]: v });

  return (
    <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-usuario`}>Usuario</Label>
        <Select
          id={`${id}-usuario`}
          value={valor.actor ?? ""}
          onChange={(e) => set("actor")(e.target.value)}
        >
          <option value="">Todos</option>
          {usuarios.map((u) => (
            <option key={u.id} value={String(u.id)}>
              {u.nombre_usuario}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-accion`}>Acción</Label>
        <Select
          id={`${id}-accion`}
          value={valor.accion ?? ""}
          onChange={(e) => set("accion")(e.target.value)}
        >
          <option value="">Todas</option>
          {(resumen?.acciones ?? []).map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-obra`}>Obra</Label>
        <Select
          id={`${id}-obra`}
          value={valor.obra ?? ""}
          onChange={(e) => set("obra")(e.target.value)}
        >
          <option value="">Todas</option>
          {obras.map((o) => (
            <option key={o.id_obra} value={String(o.id_obra)}>
              {o.codigo_obra} · {o.nombre}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-tabla`}>Tabla</Label>
        <Select
          id={`${id}-tabla`}
          value={valor.tabla ?? ""}
          onChange={(e) => set("tabla")(e.target.value)}
        >
          <option value="">Todas</option>
          {(resumen?.tablas ?? []).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-registro`}>Id registro</Label>
        <Input
          id={`${id}-registro`}
          inputMode="numeric"
          placeholder="p. ej. 128"
          value={valor.id_registro ?? ""}
          onChange={(e) => set("id_registro")(e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-desde`}>Desde</Label>
        <Input
          id={`${id}-desde`}
          type="date"
          value={valor.desde ?? ""}
          onChange={(e) => set("desde")(e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-hasta`}>Hasta</Label>
        <Input
          id={`${id}-hasta`}
          type="date"
          value={valor.hasta ?? ""}
          onChange={(e) => set("hasta")(e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-ip`}>IP</Label>
        <Input
          id={`${id}-ip`}
          placeholder="p. ej. 203.0.113.5"
          value={valor.ip ?? ""}
          onChange={(e) => set("ip")(e.target.value)}
        />
      </div>
    </div>
  );
}
